import React, { useEffect, useState } from "react";
import SetupStep from "@/components/SetupStep";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

/**
 * /setup/staples — THIRD (final) focused mini-page. Shows the regional
 * pantry presets from GET /staples/presets as warm cards; user picks
 * one and taps "Save & continue tour" which loads the preset via
 * POST /staples/load-preset and lands them on the dashboard with the
 * onboarding tour marked complete.
 *
 * Skipping still finishes the tour — nothing is one-shot; users can
 * always load a preset later from the Staples page.
 */
export default function SetupStaples() {
  const [presets, setPresets] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staples/presets");
        setPresets(data.presets || []);
      } catch { /* not fatal */ }
      finally { setLoading(false); }
    })();
  }, []);

  const saveContinue = async () => {
    if (!selectedKey) return; // allow skip through
    setSaving(true);
    try {
      const { data } = await api.post("/staples/load-preset", { preset_key: selectedKey });
      const count = data?.added ?? data?.count ?? 0;
      toast.success(
        count > 0
          ? `${count} staples added to your pantry.`
          : "Preset loaded."
      );
    } catch (err) {
      toast.error("Couldn't load that preset. You can still continue.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SetupStep
      step={3}
      eyebrow="FIRST LIGHT · STEP 4 OF 5"
      title="Fill your pantry drawer."
      subtitle="Load a regional spice preset and dozens of bowls appear in your drawer instantly. Tap the ones you have to top them up — leave the empty ones as a gentle shopping list."
      onSaveContinue={saveContinue}
      nextRoute="/family-table"
      busy={saving}
      ctaLabel={selectedKey ? "Load preset & finish" : "Finish setup"}
      skipLabel="Skip — I'll pick a preset later"
    >
      {loading ? (
        <div className="text-sm text-hearth-mute italic">Loading regional presets…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {presets.map((p) => {
            const on = selectedKey === p.key;
            return (
              <button
                key={p.key}
                data-testid={`setup-preset-${p.key}`}
                type="button"
                onClick={() => setSelectedKey(on ? null : p.key)}
                className={`text-left rounded-2xl border p-5 transition ${
                  on
                    ? "bg-hearth-clay/8 border-hearth-clay ring-2 ring-hearth-clay/30"
                    : "bg-white border-hearth-line hover:border-hearth-clay/40"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="caption text-hearth-mute tracking-widest">
                    {p.count} items
                  </div>
                  {on && <Sparkles size={14} className="text-hearth-clayDark" />}
                </div>
                <div className="font-display text-xl font-semibold text-hearth-ink mb-1">
                  {p.label}
                </div>
                <div className="text-sm text-hearth-mute leading-relaxed">
                  {p.description}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </SetupStep>
  );
}
